import { FileUpIcon, ScanLineIcon, TriangleAlertIcon } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'
import { Button, buttonVariants } from '@app/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@app/components/ui/card'
import { cn } from '@app/lib/cn'
import { formatCount } from './format'
import { ErrorNote, Notice } from './notice'

/**
 * Everything that puts a file into a draft: the sheet upload and the DXF
 * import.
 *
 * They live together because they share one rule: a file is persisted before
 * anything is derived from it, and nothing already on the screen is thrown away
 * until the replacement is safely stored.
 */

/* ── Sheet upload ───────────────────────────────────────────────────────────── */

export function ScanUploadCard({
  mode,
  sheetNumber,
  pending,
  progress,
  error,
  onFiles,
  onCancel,
}: {
  /** `add` puts a new sheet in the set; `replace` re-shoots the one on screen. */
  mode: 'first' | 'add' | 'replace'
  /** Which sheet this is — the number the strip and the canvas header show. */
  sheetNumber: number
  pending: boolean
  /** Where a multi-file queue has got to, or null when nothing is in flight. */
  progress: string | null
  error: unknown
  /**
   * The chosen sheets, in the order they were chosen. Several is the ordinary
   * case: a set is laid out over four or five beds and picked in one go, and the
   * screen uploads them one after another (see the workspace's queue).
   */
  onFiles: (files: File[]) => void
  onCancel?: () => void
}) {
  const several = mode !== 'replace'

  const title =
    mode === 'replace'
      ? `Tarama ${formatCount(sheetNumber)} — yeniden çek`
      : mode === 'add'
        ? `Tarama ${formatCount(sheetNumber)} ekle`
        : 'Tarama yükle'

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {onCancel ? (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Vazgeç
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {mode === 'replace' ? (
          <Notice tone="warn" icon={<TriangleAlertIcon aria-hidden />}>
            Yeni görüntü bu taramanın yerine geçer: yalnızca bu taramanın parçaları ve
            etiketleri silinir, izleri yeni görüntüden baştan çıkarılır. Setteki diğer
            taramalar olduğu gibi kalır.
          </Notice>
        ) : null}
        {mode === 'add' ? (
          <p className="text-data text-ink-muted">
            Set bir tarayıcı camına sığmaz. Kalan kalıpları ayrı bir tarama olarak ekleyin —
            önceki taramaların parçaları ve etiketleri yerinde kalır.
          </p>
        ) : null}

        <DropZone
          accept="image/png,image/jpeg"
          pending={pending}
          multiple={several}
          onFiles={onFiles}
          title={several ? 'Taramaları buraya bırakın' : 'Taramayı buraya bırakın'}
          hint={
            several
              ? 'PNG veya JPEG · birden çok dosya seçebilirsiniz, her biri ayrı tarama olur · kalıplar birbirine ve kenarlara değmemeli'
              : 'PNG veya JPEG · kalıplar birbirine ve kenarlara değmeyecek şekilde yerleştirilmiş olmalı'
          }
          buttonLabel={several ? 'Dosyaları seç' : 'Dosya seç'}
          icon={<ScanLineIcon className="size-5 text-ink-muted/60" aria-hidden />}
        />

        {progress ? (
          <p className="font-mono text-micro tnum text-ink-muted" aria-live="polite">
            {progress}
          </p>
        ) : null}

        <p className="text-micro text-ink-muted">
          Ölçü dosyanın kendi DPI bilgisinden okunur — fabrika tarayıcısı bunu her
          taramaya yazar. DPI bilgisi olmayan dosyaları fabrika tarayıcısından yeniden
          tarayın.
        </p>

        {error ? <ErrorNote error={error} /> : null}
      </CardContent>
    </Card>
  )
}

/* ── DXF ────────────────────────────────────────────────────────────────────── */

export function DxfImportCard({
  pending,
  error,
  masterSize,
  onFile,
}: {
  pending: boolean
  error: unknown
  masterSize: number
  onFile: (file: File) => void
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>DXF içe aktar</CardTitle>
        <span className="font-mono text-micro tnum text-ink-muted">
          ana beden {formatCount(masterSize)}
        </span>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <DropZone
          accept=".dxf,application/dxf,image/vnd.dxf,text/plain"
          pending={pending}
          onFiles={(files) => {
            const [file] = files
            if (file) onFile(file)
          }}
          title="DXF dosyasını buraya bırakın"
          hint={`Adı ${masterSize} ya da ${masterSize * 10} ile biten bloklar parça olarak açılır.`}
          buttonLabel="Dosya seç"
          icon={<FileUpIcon className="size-5 text-ink-muted/60" aria-hidden />}
        />
        {error ? <ErrorNote error={error} /> : null}
      </CardContent>
    </Card>
  )
}

/**
 * What the DXF reader had to interpret on the way in.
 *
 * The lines themselves are the parser's English diagnostics, so they are
 * labelled as technical detail and folded away rather than dressed up as
 * instructions: the sentence that tells the operator what to do is Turkish and
 * above them.
 */
export function DxfWarnings({ warnings }: { warnings: readonly string[] }) {
  if (warnings.length === 0) return null
  return (
    <div className="rounded-sm border border-hardal/30 bg-hardal/10 px-3 py-2 text-data text-hardal">
      <p className="flex items-start gap-1.5">
        <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        <span>
          DXF okundu, ancak {formatCount(warnings.length)} yerde geometri yorumlanarak
          alındı. Parçaları kalıplarla karşılaştırın; şüpheliyse dosyayı kapalı polyline
          olarak yeniden dışa aktarın.
        </span>
      </p>
      <details className="mt-1.5">
        <summary className="cursor-pointer text-micro text-hardal/85">
          Teknik ayrıntı (İngilizce)
        </summary>
        <ul className="mt-1 flex flex-col gap-0.5 font-mono text-micro">
          {warnings.map((warning, index) => (
            <li key={`${index}-${warning}`}>{warning}</li>
          ))}
        </ul>
      </details>
    </div>
  )
}

/* ── Drop zone ──────────────────────────────────────────────────────────────── */

export function DropZone({
  accept,
  pending,
  multiple = false,
  onFiles,
  title,
  hint,
  buttonLabel,
  icon,
}: {
  accept: string
  pending: boolean
  /**
   * Whether a whole selection is taken at once. A saya set is several sheets,
   * so the sheet upload says yes; "yeniden çek" replaces ONE sheet and a DXF
   * import is one file, so those say nothing and keep the single-file input.
   */
  multiple?: boolean
  /** Never empty — the zone only calls when at least one file came through. */
  onFiles: (files: File[]) => void
  title: string
  hint: string
  buttonLabel: string
  icon: ReactNode
}) {
  const [dragging, setDragging] = useState(false)
  const inputId = useId()

  /*
   * A single-file zone that is HANDED several — a multi-file drop onto the
   * replace zone — takes the first and ignores the rest, rather than quietly
   * uploading four sheets where the operator asked to re-shoot one.
   */
  function accepted(list: FileList): File[] {
    const files = [...list]
    return multiple ? files : files.slice(0, 1)
  }

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault()
        if (!pending) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault()
        setDragging(false)
        /*
         * A drop is refused while an upload is running, exactly as the file
         * input is. Only the input carried that guard, so a second selection
         * DROPPED onto the zone mid-queue used to start a second queue beside
         * the first: two loops sharing one progress line, one clearing it while
         * the other was still sending, and a sheet cap planned against a count
         * that was already out of date.
         */
        if (pending) return
        const files = accepted(event.dataTransfer.files)
        if (files.length > 0) onFiles(files)
      }}
      className={cn(
        'flex flex-col items-center gap-2 rounded-sm border border-dashed px-6 py-10 text-center transition-colors',
        dragging ? 'border-kordovan bg-kordovan/6' : 'border-hairline bg-kagit',
      )}
    >
      {icon}
      <p className="font-display text-[0.9375rem] font-semibold tracking-tight text-ink">
        {pending ? 'Yükleniyor…' : title}
      </p>
      <p className="max-w-sm text-micro text-ink-muted">{hint}</p>
      <label
        htmlFor={inputId}
        className={cn(
          buttonVariants({ variant: 'outline', size: 'sm' }),
          'mt-1 cursor-pointer',
          pending && 'pointer-events-none opacity-45',
        )}
      >
        {buttonLabel}
      </label>
      <input
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={pending}
        className="sr-only"
        onChange={(event) => {
          const files = event.target.files ? accepted(event.target.files) : []
          if (files.length > 0) onFiles(files)
          // So picking the same file twice still fires a change.
          event.target.value = ''
        }}
      />
    </div>
  )
}
